import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import App from "./App.tsx";
import "./index.css";
// i18n must be imported before any component that uses translations
import "./i18n/config";
import { initAnalytics } from "./lib/analytics";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enableLogs: true,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
    Sentry.consoleLoggingIntegration({
      levels: ["warn", "error"],
    }),
  ],
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

initAnalytics();

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={({ resetError }) => (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 32, textAlign: "center", fontFamily: "system-ui, sans-serif" }}>
        <h1 style={{ fontSize: 24, marginBottom: 8 }}>Something went wrong</h1>
        <p style={{ color: "#666", marginBottom: 24 }}>An unexpected error occurred. Our team has been notified.</p>
        <button onClick={resetError} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#111", color: "#fff", cursor: "pointer", fontSize: 16 }}>
          Try again
        </button>
      </div>
    )}
  >
    <App />
  </Sentry.ErrorBoundary>
);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js");

      // Check for updates every 24 hours
      setInterval(() => {
        registration.update();
      }, 24 * 60 * 60 * 1000);

      // When a new SW is installed & waiting, auto-activate it
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            // New version available – reload to activate
            window.location.reload();
          }
        });
      });
    } catch {
      // SW registration failed
    }
  });

  // If the controlling SW changes (e.g. skipWaiting), reload
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    window.location.reload();
  });
}
